import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Paperclip, Plus, Search } from "lucide-react";
import CustomSelect from "../ui/CustomSelect";
import Toast from "../ui/Toast";
import Report20DataLoadingOverlay from "./Report20DataLoadingOverlay";
import DisconnectRequestDetailModal from "../modals/DisconnectRequestDetailModal";
import {
  listDisconnectRequests,
  DISCONNECT_REASONS,
  DISCONNECT_STATUS,
  statusLabel,
} from "../../services/disconnectService";
import { useAuth } from "../../hooks/useAuth";
import { canMutateTenantData } from "../../config/constants";

function formatMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "$0";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);
}

function formatMoneyExact(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "$0.00";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

function StatusBadge({ status }) {
  const label = statusLabel(status);
  let cls =
    "bg-sky-100 text-sky-900 border-sky-200";
  if (status === DISCONNECT_STATUS.CONFIRMED) {
    cls = "bg-amber-100 text-amber-900 border-amber-200";
  } else if (status === DISCONNECT_STATUS.BILLING_STOPPED) {
    cls = "bg-emerald-100 text-emerald-900 border-emerald-200";
  }
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${cls}`}
    >
      {label}
    </span>
  );
}

function BillingCheckCell({ row }) {
  const check = row.billing_check;
  if (check === "billing_stopped" || row.status === DISCONNECT_STATUS.BILLING_STOPPED) {
    const importDate = row.billing_check_at
      ? String(row.billing_check_at).slice(0, 10)
      : null;
    return (
      <div>
        <span className="text-[12px] font-medium text-[var(--good,#1f6b34)]">
          Billing stopped
        </span>
        {importDate ? (
          <p className="text-[11px] text-[var(--good,#1f6b34)]">
            Verified on {importDate}
          </p>
        ) : null}
      </div>
    );
  }
  if (check === "still_billing") {
    const importDate = row.billing_check_at
      ? String(row.billing_check_at).slice(0, 10)
      : null;
    return (
      <div>
        <span className="text-[12px] font-medium text-[var(--crit,#c0262b)]">
          Still billing
        </span>
        {importDate ? (
          <p className="text-[11px] text-[var(--crit,#c0262b)]">
            Found on import {importDate}
          </p>
        ) : null}
        {(() => {
          const creditMrc =
            row.credit_mrc != null ? row.credit_mrc : row.total_mrc;
          return creditMrc != null ? (
            <p className="text-[11px] text-[var(--crit,#c0262b)]">
              About {formatMoneyExact(creditMrc)} credit owed
            </p>
          ) : null;
        })()}
      </div>
    );
  }
  return (
    <div>
      <span className="text-[12px] text-[var(--muted,#6b6a66)]">
        Waiting on confirmation
      </span>
      {row.days_open != null ? (
        <p className="text-[11px] text-[var(--muted,#6b6a66)]">
          {row.days_open} day{row.days_open === 1 ? "" : "s"} since submission
        </p>
      ) : null}
    </div>
  );
}

function KpiCard({
  label,
  value,
  sublabel,
  valueClassName = "",
  active = false,
  onClick,
}) {
  const clickable = typeof onClick === "function";
  const Comp = clickable ? "button" : "div";
  return (
    <Comp
      type={clickable ? "button" : undefined}
      onClick={onClick}
      className={`rounded-[10px] border px-4 py-3 min-w-0 text-left transition-colors ${
        active
          ? "border-[var(--accent,#a51c1c)] bg-[var(--accent-soft,#fbe4e4)] ring-1 ring-[var(--accent,#a51c1c)]/30"
          : "border-[var(--line,#e4e2dc)] bg-white"
      } ${
        clickable
          ? "cursor-pointer hover:border-[var(--accent,#a51c1c)]/60 hover:bg-[var(--sunken,#eeece7)]/50"
          : ""
      }`}
    >
      <p className="text-[11px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)]">
        {label}
      </p>
      <p
        className={`mt-1 text-[22px] font-semibold tabular-nums leading-tight text-[var(--ink,#1c1c1a)] ${valueClassName}`}
      >
        {value}
      </p>
      {sublabel ? (
        <p className="mt-0.5 text-[12px] text-[var(--muted,#6b6a66)]">{sublabel}</p>
      ) : null}
    </Comp>
  );
}

const TABS = [
  { id: "all", label: "All", countKey: "all_count" },
  { id: "open", label: "Open", countKey: "open_requests" },
  { id: "waiting_on_supplier", label: "Waiting on supplier", countKey: "waiting_on_supplier" },
  { id: "still_billing", label: "Still billing", countKey: "still_billing" },
  { id: "missing_evidence", label: "Missing evidence", countKey: "missing_evidence" },
  { id: "completed", label: "Completed", countKey: "completed_count" },
];

const Report20DisconnectsView = ({ tenantId, onNewDisconnect }) => {
  const { userRole } = useAuth();
  const canEdit = canMutateTenantData(userRole);
  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [provider, setProvider] = useState("all");
  const [reason, setReason] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [openRequestId, setOpenRequestId] = useState(null);
  const [toast, setToast] = useState({
    title: "",
    message: null,
    type: "success",
  });
  const clearToast = useCallback(() => {
    setToast({
      title: "",
      message: null,
      type: "success",
    });
  }, []);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError("");
    try {
      const res = await listDisconnectRequests(tenantId, {
        tab,
        search: search.trim() || undefined,
        provider: provider === "all" ? undefined : provider,
        reason: reason === "all" ? undefined : reason,
        limit: 100,
      });
      setItems(res?.data?.items || []);
      setStats(res?.data?.stats || {});
    } catch (err) {
      setError(err.message || "Failed to load disconnects");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [tenantId, tab, search, provider, reason]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  const providers = useMemo(() => {
    const set = new Set();
    items.forEach((r) => {
      if (r.provider) set.add(r.provider);
    });
    return Array.from(set).sort();
  }, [items]);

  const resetFilters = () => {
    setSearch("");
    setProvider("all");
    setReason("all");
    setTab("all");
  };

  return (
    <div className="relative">
      <Toast
        title={toast.title}
        message={toast.message}
        type={toast.type}
        duration={5000}
        onClose={clearToast}
      />
      <Report20DataLoadingOverlay show={loading} />

      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mb-5">
        <div>
          <p className="text-[12px] font-semibold uppercase tracking-[0.05em] text-[var(--accent,#a51c1c)]">
            Reports
          </p>
          <h1 className="mt-1 text-[26px] font-semibold leading-tight text-[var(--ink,#1c1c1a)]">
            Disconnects
          </h1>
          <p className="mt-1 max-w-2xl text-[13px] text-[var(--muted,#6b6a66)]">
            Every disconnect request, the supplier&apos;s confirmation, the
            evidence on file and whether billing actually stopped.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {typeof onNewDisconnect === "function" ? (
            <button
              type="button"
              onClick={onNewDisconnect}
              className="inline-flex items-center gap-1.5 rounded-md bg-[var(--accent,#a51c1c)] px-3 py-2 text-[13px] font-medium text-white hover:opacity-90"
            >
              <Plus className="h-4 w-4" />
              New disconnect
            </button>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 mb-5">
        <KpiCard
          label="Open requests"
          value={stats.open_requests ?? 0}
          sublabel={`${formatMoneyExact(stats.open_mrc)} MRC being removed`}
          active={tab === "open"}
          onClick={() => setTab("open")}
        />
        <KpiCard
          label="Waiting on supplier"
          value={stats.waiting_on_supplier ?? 0}
          sublabel={
            stats.oldest_waiting_days != null
              ? `Oldest submitted ${stats.oldest_waiting_days} days ago`
              : "No open submissions"
          }
          active={tab === "waiting_on_supplier"}
          onClick={() => setTab("waiting_on_supplier")}
        />
        <KpiCard
          label="Still billing"
          value={stats.still_billing ?? 0}
          valueClassName={
            stats.still_billing > 0 ? "text-[var(--crit,#c0262b)]" : ""
          }
          sublabel={
            stats.still_billing > 0
              ? `About ${formatMoneyExact(stats.still_billing_mrc)} credit owed`
              : "None past last expected bill"
          }
          active={tab === "still_billing"}
          onClick={() => setTab("still_billing")}
        />
        <KpiCard
          label="Missing evidence"
          value={stats.missing_evidence ?? 0}
          sublabel="Requests with no file attached"
          active={tab === "missing_evidence"}
          onClick={() => setTab("missing_evidence")}
        />
        <KpiCard
          label="Removed this year"
          value={formatMoney(stats.removed_mrc_this_year)}
          sublabel={`MRC a month · about ${formatMoney(
            (Number(stats.removed_mrc_this_year) || 0) * 12
          )} a year`}
          active={tab === "completed"}
          onClick={() => setTab("completed")}
        />
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-1 border-b border-[var(--line,#e4e2dc)]">
        {TABS.map((t) => {
          const count = stats[t.countKey];
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`px-3 py-2 text-[13px] font-medium border-b-2 -mb-px ${
                active
                  ? "border-[var(--accent,#a51c1c)] text-[var(--accent,#a51c1c)]"
                  : "border-transparent text-[var(--muted,#6b6a66)] hover:text-[var(--ink,#1c1c1a)]"
              }`}
            >
              {t.label}
              {count != null ? ` ${count}` : ""}
            </button>
          );
        })}
      </div>

      <div className="mb-4 flex flex-col gap-2 lg:flex-row lg:items-center">
        <div className="relative flex-1 min-w-0">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--muted,#6b6a66)]" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Circuit, tracking number, address or request"
            className="w-full rounded-md border border-[var(--line,#e4e2dc)] bg-white py-2 pl-9 pr-3 text-[13px] focus:outline-none focus:ring-1 focus:ring-[var(--accent,#a51c1c)]"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="w-[160px]">
            <CustomSelect
              value={provider}
              onChange={(e) => setProvider(e?.target?.value || "all")}
              options={[
                { value: "all", label: "Provider: All" },
                ...providers.map((p) => ({ value: p, label: p })),
              ]}
            />
          </div>
          <div className="w-[180px]">
            <CustomSelect
              value={reason}
              onChange={(e) => setReason(e?.target?.value || "all")}
              options={[
                { value: "all", label: "Reason: All" },
                ...DISCONNECT_REASONS.map((r) => ({ value: r, label: r })),
              ]}
            />
          </div>
          <button
            type="button"
            onClick={resetFilters}
            className="rounded-md border border-[var(--line-strong,#d5d3cc)] bg-white px-3 py-2 text-[13px] font-medium hover:bg-[var(--sunken,#eeece7)]"
          >
            Reset
          </button>
        </div>
      </div>

      {error ? (
        <p className="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-800">
          {error}
        </p>
      ) : null}

      <div className="overflow-x-auto rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white">
        <table className="w-full min-w-[1100px] text-left text-[13px]">
          <thead className="bg-[var(--sunken,#eeece7)] text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--muted,#6b6a66)]">
            <tr>
              <th className="px-3 py-2.5">Request</th>
              <th className="px-3 py-2.5">Circuit</th>
              <th className="px-3 py-2.5">Address</th>
              <th className="px-3 py-2.5">Submitted</th>
              <th className="px-3 py-2.5">Tracking number</th>
              <th className="px-3 py-2.5">Status</th>
              <th className="px-3 py-2.5">Days open</th>
              <th className="px-3 py-2.5 text-right">MRC removed</th>
              <th className="px-3 py-2.5">Billing check</th>
              <th className="px-3 py-2.5">Evidence</th>
            </tr>
          </thead>
          <tbody>
            {!loading && items.length === 0 ? (
              <tr>
                <td
                  colSpan={10}
                  className="px-3 py-10 text-center text-[var(--muted,#6b6a66)]"
                >
                  No disconnect requests yet. Start from Detailed Inventory →
                  edit a circuit → Disconnect.
                </td>
              </tr>
            ) : (
              items.map((row) => {
                const requestId = row.request_id || row.id;
                const circuitLabel =
                  row.circuit_id || row.primary_circuit_id || "—";
                const siteLabel =
                  row.site_address || row.primary_address || "—";
                const mrcValue =
                  row.mrc != null ? row.mrc : row.total_mrc;
                return (
                  <tr
                    key={
                      row.circuit_row_id != null
                        ? `dc-${row.circuit_row_id}`
                        : `${requestId}-${circuitLabel}`
                    }
                    role="button"
                    tabIndex={0}
                    onClick={() => setOpenRequestId(requestId)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setOpenRequestId(requestId);
                      }
                    }}
                    className="border-t border-[var(--line,#e4e2dc)] align-top cursor-pointer hover:bg-[var(--sunken,#eeece7)]/60"
                  >
                    <td className="px-3 py-3">
                      <p className="font-semibold text-[var(--accent,#a51c1c)] hover:underline">
                        {row.request_number}
                      </p>
                      <p className="text-[12px] text-[var(--muted,#6b6a66)]">
                        {row.provider}
                        {row.reason ? ` · ${row.reason}` : ""}
                      </p>
                    </td>
                    <td className="px-3 py-3">
                      <p className="font-medium">{circuitLabel}</p>
                    </td>
                    <td className="px-3 py-3 text-[var(--muted,#6b6a66)] max-w-[200px]">
                      {siteLabel}
                    </td>
                    <td className="px-3 py-3 tabular-nums">
                      {row.submitted_date
                        ? String(row.submitted_date).slice(0, 10)
                        : row.created_at
                          ? String(row.created_at).slice(0, 10)
                          : "—"}
                    </td>
                    <td className="px-3 py-3">
                      {row.tracking_display ? (
                        row.tracking_display
                      ) : (
                        <span className="text-amber-800">Not yet</span>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <StatusBadge status={row.status} />
                    </td>
                    <td className="px-3 py-3 tabular-nums">
                      {row.days_open != null ? row.days_open : "—"}
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums font-medium">
                      {formatMoneyExact(mrcValue)}
                    </td>
                    <td className="px-3 py-3">
                      <BillingCheckCell row={row} />
                    </td>
                    <td className="px-3 py-3">
                      {row.evidence_count > 0 ? (
                        <span className="inline-flex items-center gap-1 text-[12px] font-medium text-[var(--ink,#1c1c1a)]">
                          <Paperclip className="h-3.5 w-3.5" />
                          {row.evidence_count}
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-900">
                          None
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {openRequestId ? (
        <DisconnectRequestDetailModal
          requestId={openRequestId}
          tenantId={tenantId}
          readOnly={!canEdit}
          onClose={() => setOpenRequestId(null)}
          onSaved={() => {
            setToast({
              title: "Saved",
              message: "Disconnect request changes saved successfully.",
              type: "success",
            });
            load();
          }}
        />
      ) : null}
    </div>
  );
};

export default Report20DisconnectsView;
